// Sentinel id <-> NomOS record id, via the sentinel_*_id attributes.
// Batched (one `in` query per tick per type) and cached for the session, keyed by NomOS base + tenant (B7).
import { getConfig, queryRecords, registerTenantCache, scalar } from './client';

export type IdKind = 'building' | 'meter' | 'asset' | 'task';

const KIND: Record<IdKind, { type: string; attr: string }> = {
  building: { type: 'site', attr: 'sentinel_building_id' },
  meter: { type: 'meter', attr: 'sentinel_meter_id' },
  asset: { type: 'asset', attr: 'sentinel_asset_id' },
  task: { type: 'task', attr: 'sentinel_task_id' },
};

const toNomos = new Map<string, string>();   // `${base}|${tenant}|${kind}:${sentinelId}` -> record_id
const toSentinel = new Map<string, string>(); // `${base}|${tenant}|${kind}:${recordId}` -> sentinelId
const pending = new Map<string, { ids: Set<string>; waiters: Array<() => void> }>(); // `${base}|${tenant}|${kind}`

export function clearIdCache(): void { toNomos.clear(); toSentinel.clear(); pending.clear(); }
registerTenantCache(clearIdCache);

/** Cache scope: the same Sentinel id may exist in several tenants, so every key carries base + tenant. */
function scope(): string { const c = getConfig(); return `${c.baseUrl}|${c.tenant}`; }

/** Seed the cache from rows already fetched (e.g. list responses). */
export function rememberId(kind: IdKind, sentinelId: string, recordId: string, sc: string = scope()): void {
  toNomos.set(`${sc}|${kind}:${sentinelId}`, recordId);
  toSentinel.set(`${sc}|${kind}:${recordId}`, sentinelId);
}

async function flush(kind: IdKind, ids: string[], sc: string): Promise<void> {
  const { type, attr } = KIND[kind];
  const { rows } = await queryRecords(type, {
    filter: { attribute: attr, op: 'in', value: ids }, fields: [attr], limit: Math.max(ids.length, 1) });
  const matched = new Set<string>();
  for (const r of rows) {
    const sid = scalar(r.values[attr]);
    if (typeof sid === 'string') { rememberId(kind, sid, r.record_id, sc); matched.add(sid); } // the scope the batch was queued in
  }
  // A record made in NomOS (e.g. a site created in the UI) has no Sentinel id: the screens know it by its record id.
  const recordIds = ids.filter((id) => !matched.has(id) && UUID_RE.test(id));
  // Ask by identity (the `record_id` filter), not by the Sentinel-id cell: records/values leaves out a record
  // with no cell for the asked attribute, which is exactly such a record. Absent/archived ids are left out.
  if (recordIds.length) {
    const found = await queryRecords(type, { filter: { attribute: 'record_id', op: 'in', value: recordIds }, limit: recordIds.length });
    for (const r of found.rows) toNomos.set(`${sc}|${kind}:${r.record_id}`, r.record_id);
  }
}
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Resolve many Sentinel ids to NomOS record ids; unknown ids are absent from the result. */
export async function resolveRecordIds(kind: IdKind, sentinelIds: string[]): Promise<Map<string, string>> {
  const sc = scope();
  const missing = sentinelIds.filter((s) => !toNomos.has(`${sc}|${kind}:${s}`));
  if (missing.length) {
    const pk = `${sc}|${kind}`;
    let batch = pending.get(pk);
    const isNew = !batch;
    if (!batch) { batch = { ids: new Set(), waiters: [] }; pending.set(pk, batch); }
    missing.forEach((m) => batch!.ids.add(m));
    const done = new Promise<void>((resolve) => { batch!.waiters.push(resolve); });
    if (isNew) {
      const b = batch;
      queueMicrotask(async () => {
        pending.delete(pk);
        try { await flush(kind, [...b.ids], sc); } finally { b.waiters.forEach((w) => w()); }
      });
    }
    await done;
  }
  const out = new Map<string, string>();
  for (const s of sentinelIds) { const r = toNomos.get(`${sc}|${kind}:${s}`); if (r) out.set(s, r); }
  return out;
}

export async function resolveRecordId(kind: IdKind, sentinelId: string): Promise<string | undefined> {
  return (await resolveRecordIds(kind, [sentinelId])).get(sentinelId);
}

/** Reverse: NomOS record id -> Sentinel id (cache only; fill via rememberId from fetched rows). */
export function sentinelIdFor(kind: IdKind, recordId: string): string | undefined {
  return toSentinel.get(`${scope()}|${kind}:${recordId}`);
}
